// @ts-check
/**
 * The Executive Summary itself, laid out for the AI panel. Presentation only: it renders whatever the
 * report contains and omits the sections the report leaves empty, so a stable organisation gets a short
 * panel rather than a set of empty headings.
 *
 * Two pieces of copy here are fixed UI text, not model output - the no-diagnosis note under "Why this
 * matters" and the non-causation note under the events. The agent is told not to restate them.
 */
import React from 'react';
import PropTypes from 'prop-types';

import { TestableText } from '@components/general/TestableComponents';
import AIReportChanges from './AIReportChanges.jsx';
import AIReportEvents from './AIReportEvents.jsx';
import AIReportFocus from './AIReportFocus.jsx';
import AIReportSection from './AIReportSection.jsx';
import AIReportSteps from './AIReportSteps.jsx';
import { renderReportMarkdown } from './AIReportMarkdown.jsx';

/** @typedef {import('@datahooks/types/organisationAiReport.types.js').AiReportBody} AiReportBody */
/** @typedef {import('@datahooks/ProjectsListContext').BasicProject} BasicProject */

export const NO_DIAGNOSIS_NOTE = 'This report does not diagnose individual test failures. Navigate to the project to investigate the underlying failed areas and associated events.';

/**
 * Renders the body of an AI report, including the executive summary, recommended focus, significant changes, recent project events, and recommended investigation.
 * 
 * @param {{ report: AiReportBody | null, projects?: BasicProject[] }} props
 * @returns {JSX.Element|null}
 */
const AIReportBody = ({ report, projects = [] }) => {
  if (!report) {
    return null;
  }

  const {
    executiveSummary,
    recommendedFocus,
    significantChanges = [],
    whyThisMatters,
    recentProjectEvents = [],
    recommendedInvestigation = [],
  } = report;

  return (
    <div className="flex flex-col gap-5" data-testid="ai-report">
      <div
        className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300"
        data-testid="ai-report-summary"
      >
        {renderReportMarkdown(executiveSummary, 'ai-report-summary')}
      </div>

      <AIReportFocus focus={recommendedFocus} projects={projects} />

      {significantChanges.length > 0 && (
        <AIReportSection title="Significant changes" dataTestId="ai-report-section-changes">
          <AIReportChanges changes={significantChanges} projects={projects} />
        </AIReportSection>
      )}

      <AIReportSection title="Why this matters" dataTestId="ai-report-section-why">
        <div className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300" data-testid="ai-report-why">
          {renderReportMarkdown(whyThisMatters, 'ai-report-why')}
          <TestableText variant="small" dataTestId="ai-report-why-note">{NO_DIAGNOSIS_NOTE}</TestableText>
        </div>
      </AIReportSection>

      {recentProjectEvents.length > 0 && (
        <AIReportSection title="Recent project events" dataTestId="ai-report-section-events">
          <AIReportEvents events={recentProjectEvents} projects={projects} />
        </AIReportSection>
      )}

      {recommendedInvestigation.length > 0 && (
        <AIReportSection title="Recommended investigation" dataTestId="ai-report-section-investigation">
          <AIReportSteps steps={recommendedInvestigation} projects={projects} />
        </AIReportSection>
      )}
    </div>
  );
};

AIReportBody.propTypes = {
  report: PropTypes.shape({
    isStable: PropTypes.bool,
    executiveSummary: PropTypes.string,
    recommendedFocus: PropTypes.object,
    significantChanges: PropTypes.array,
    whyThisMatters: PropTypes.string,
    recentProjectEvents: PropTypes.array,
    recommendedInvestigation: PropTypes.array,
  }),
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    title: PropTypes.string,
  })),
};

export default AIReportBody;
